import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CountryCode, PaymentType, Period } from 'src/app/_models';
import { PriceService } from '../price/price.service';
import { RegionService } from '../region/region.service';

@Injectable({
  providedIn: 'root',
})
export class FormServiceService {
  signUpForm: FormGroup;
  paymentForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private priceService: PriceService,
    private regionService: RegionService,
  ) {
    const regexEmailValidatorString =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

    this.signUpForm = this.fb.group({
      dob: new FormControl('', this.dobRegionValidation(this.regionService.getClientRegion())),
      firstName: new FormControl('', Validators.required),
      middleName: new FormControl(''),
      preferredName: new FormControl(''),
      lastName: new FormControl('', Validators.required),
      address: new FormControl('', Validators.required),
      postcode: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(5)]),
      city: new FormControl('', Validators.required),
      phoneNumber: new FormControl('', Validators.required),
      phoneVerificationCode: new FormControl('', [Validators.required, Validators.minLength(6)]),
      countryCode: new FormControl(
        { value: this.regionService.getClientRegion(), disabled: false },
        Validators.required,
      ),
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern(regexEmailValidatorString),
      ]),
      emailVerificationCode: new FormControl(''),
      country: new FormControl({ value: this.regionService.getClientRegion(), disabled: false }, Validators.required),
    });

    this.paymentForm = this.fb.group({
      paymentFrequency: new FormControl('', Validators.required),
      paymentMethod: new FormControl(PaymentType.V2),
      cardPayment: new FormControl(''),
      discountCode: new FormControl(''),
      startDate: new FormControl(),
      acceptTermsAndConditions: new FormControl(false, Validators.requiredTrue),
    });
    this.paymentForm.controls['paymentFrequency'].valueChanges.subscribe((value) => {
      this.updatePaymentFrequency(value);
    });
  }

  getEmailRegex() {
    return /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  }

  async updatePaymentFrequency(paymentFrequency: Period) {
    localStorage.setItem('paymentFrequency', paymentFrequency);
    const discount = await this.priceService.getActiveDiscount();
    const template = await this.priceService.getActiveTemplate();
    if (discount && template) {
      this.priceService.setPriceFromActiveDiscountAndDiscountTemplate(paymentFrequency);
    } else if (template) {
      this.priceService.setPriceFromActiveTemplate(paymentFrequency);
    } else {
      return;
    }
  }

  dobRegionValidation(region: CountryCode) {
    switch (region) {
      case CountryCode.SE:
        return [Validators.required, Validators.minLength(12), Validators.maxLength(12)];
      case CountryCode.NO:
        return [Validators.required, Validators.minLength(8), Validators.maxLength(8)];
      default:
        return Validators.required;
    }
  }

  initilizePaymentFrequency(): Period | undefined {
    const paymentFrequency = localStorage.getItem('paymentFrequency');
    if (paymentFrequency) {
      this.paymentForm.controls['paymentFrequency'].setValue(paymentFrequency);
      return paymentFrequency as Period;
    }
    return undefined;
  }

  setSignUpForm(form: FormGroup) {
    this.signUpForm.patchValue(form);
  }

  isInputValid(controlName: string): string {
    const control = this.signUpForm.get(controlName);
    if (!control) {
      return '';
    }

    if (control.invalid && control.dirty) {
      return 'border-font_error_bright';
    }

    if (control.invalid && control.touched) {
      return 'border-font_error_bright';
    }
    if (control.valid) {
      return 'border-font_success';
    }
    return '';
  }

  isFormValid(): boolean {
    return this.signUpForm.valid;
  }

  storeSignUpFormToSessionStorage() {
    sessionStorage.setItem('signUpForm', JSON.stringify(this.signUpForm.getRawValue()));
  }

  getSignUpFormFromSessionStorage(control?: string) {
    const form = sessionStorage.getItem('signUpForm');
    if (form && control) {
      const formControl = JSON.parse(form)[control];
      return formControl;
    } else if (form) {
      return JSON.parse(form);
    } else {
      return null;
    }
  }

  getSignUpForm(): FormGroup {
    return this.signUpForm;
  }

  getForm() {
    return this.paymentForm;
  }

  getUser() {
    const personalDetails = this.paymentForm.get('personalDetails')?.value;
    const firstName = personalDetails.firstName;
    const lastName = personalDetails.lastName;
    return `${firstName} ${lastName}`;
  }

  getTrimmedFormValue(control: AbstractControl | null): string | undefined {
    return control && control.value && typeof control.value === 'string' ? control.value.trim() : undefined;
  }
}
